import { Ban, Eye, RotateCcw, Star, Trash2 } from 'lucide-react'
import React, { useCallback, useEffect } from 'react'
import Pagination from './Pagination'
import { UpdateStatusAPI } from '../redux/actions/user-management/UpdateStatusAction';
import { showErrorToast, showSuccessToast } from '../helpers/SweetAlert';
import { updateStatusStateReset } from '../redux/slices/user-management/UpdateStatusSlice';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { DeleteUserAPI } from '../redux/actions/user-management/DeleteUserAction';
import { deleteUserStateReset } from '../redux/slices/user-management/DeleteUserSlice';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/AxiosInstance';
import { endpoints } from '../services/endpoints';

const Table = ({ title, tableData, setDataList, columns, limit, items, totalPages, currentPage, handlePageChange, setLimit, setDeleted }) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { response: updateStatusResponse, error: updateStatusError } = useSelector((state) => state.updateUserStatus);
    const { response: deleteUserResponse, error: deleteUserError } = useSelector((state) => state.deleteUser);

    const handleFeatureUser = useCallback(async (data) => {
        try {
            const shouldFeature = !data.featuredUser;
            let featureDuration = '1_week';

            if (shouldFeature) {
                const result = await Swal.fire({
                    title: 'Feature user',
                    text: 'Select how long this user should be featured.',
                    icon: 'info',
                    input: 'select',
                    inputOptions: {
                        '1_day': '1 Day',
                        '1_week': '1 Week',
                        '1_month': '1 Month',
                        '1_year': '1 Year',
                    },
                    inputValue: featureDuration,
                    showCancelButton: true,
                    confirmButtonText: 'Feature',
                    cancelButtonText: 'Cancel',
                });

                if (!result.isConfirmed) return;
                featureDuration = result.value || '1_week';
            } else {
                const result = await Swal.fire({
                    title: 'Remove featured user?',
                    text: 'This user will no longer appear as featured.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Remove',
                    cancelButtonText: 'Cancel',
                });

                if (!result.isConfirmed) return;
            }

            const response = await axiosInstance.post(endpoints.updateUserFeature, {
                user_id: data._id,
                featured_user: shouldFeature,
                feature_duration: featureDuration,
            });
            showSuccessToast(response?.data?.message || 'User feature updated successfully');
            setDataList((prevList) =>
                prevList.map((item) => item._id === data._id ? { ...item, featuredUser: shouldFeature } : item)
            );
        } catch (error) {
            showErrorToast(error?.response?.data?.message || 'Unable to update user feature');
        }
    }, [setDataList]);

    const toggleStatus = useCallback(async (data, duration = 'permanent') => {
        const newStatus = data.status === 'active' ? 'banned' : 'active';
        try {
            if (newStatus === 'banned') {
                const result = await Swal.fire({
                    title: 'Suspend user',
                    text: 'Select how long this user should be suspended.',
                    icon: 'warning',
                    input: 'select',
                    inputOptions: {
                        '1_day': '1 Day',
                        '1_week': '1 Week',
                        '1_month': '1 Month',
                        permanent: 'Permanent',
                    },
                    inputValue: duration,
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#6c757d',
                    confirmButtonText: 'Suspend',
                    cancelButtonText: 'Cancel',
                });

                if (!result.isConfirmed) return;

                await dispatch(UpdateStatusAPI({
                    user_id: data._id,
                    status: newStatus,
                    suspension_duration: result.value || 'permanent'
                }));
                setDataList((prevList) =>
                    prevList.map((item) => item._id === data._id ? { ...item, status: newStatus } : item)
                );
                return;
            }

            Swal.fire({
                title: 'Are you sure?',
                text: `You want to reactivate this user`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes',
                cancelButtonText: 'No',
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        await dispatch(UpdateStatusAPI({ user_id: data._id, status: newStatus }));
                        setDataList((prevList) =>
                            prevList.map((item) => {
                                return item._id === data._id ? { ...item, status: newStatus } : item
                            }
                            )
                        );
                    } catch (error) {
                        Swal.fire(error?.response?.message);
                    }
                }
            });

        } catch (error) {
            console.error('Error toggling status:', error);
        }
    }, [dispatch]);

    const handleDelete = useCallback(async (d) => {
        Swal.fire({
            title: 'Are you sure?',
            text: `You want to delete`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await dispatch(DeleteUserAPI(d._id));
                } catch (error) {
                    Swal.fire(error?.response?.message);
                }
            }
            // else if (result.dismiss === Swal.DismissReason.cancel) {
            //     Swal.fire('Cancelled');
            // }
        });
    }, [dispatch]);

    const renderRow = (row) => {
        return columns?.map((col, colIndex) => {
            switch (col) {
                case 'User':
                    return <td key={colIndex}>{row?.username || 'N/A'}</td>;
                case 'Email':
                    return <td key={colIndex}>{row?.email || 'N/A'}</td>;
                case 'Status':
                    return <td key={colIndex}>{row?.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'N/A'}</td>;
                case 'Registeration':
                    return <td key={colIndex}>{row?.createdAt || 'N/A'}</td>;
                case 'Actions':
                    return (
                        <td key={colIndex}>
                            <span className="td-action">
                                {/* <button className="td-edit-btn" data-bs-toggle="modal" data-bs-target="#editProject" disabled style={{ cursor: 'not-allowed' }}>
                                    <FilePenLine />
                                </button> */}
                                <button className="td-view-btn" onClick={() => navigate(`/admin-dashboard/user-detail/${row._id}`, {
                                    state: { id: row._id, from: '/admin-dashboard/user-management' }
                                })}>
                                    <Eye />
                                </button>
                                <button className="td-delete-btn" onClick={() => handleDelete(row)}>
                                    <Trash2 />
                                </button>
                                <button className="td-delete-btn" title={row?.status === 'banned' ? 'Reactivate User' : 'Suspend User'} onClick={() => toggleStatus(row)}>
                                    {row?.status === 'banned' ? <RotateCcw /> : <Ban />}
                                </button>
                                <button className="td-view-btn" title={row?.featuredUser ? 'Remove Featured User' : 'Feature User'} onClick={() => handleFeatureUser(row)}>
                                    <Star fill={row?.featuredUser ? 'currentColor' : 'none'} />
                                </button>
                            </span>
                        </td>
                    )
                default:
                    return <td key={colIndex}>N/A</td>;
            }
        });
    };

    useEffect(() => {
        if (updateStatusResponse?.status === true) {
            showSuccessToast(updateStatusResponse?.message);
            dispatch(updateStatusStateReset());
        }
        if (updateStatusError) {
            showErrorToast(updateStatusError?.data);
            dispatch(updateStatusStateReset());
        }
    }, [updateStatusResponse, updateStatusError])


    useEffect(() => {
        if (deleteUserResponse?.status === true) {
            Swal.fire(deleteUserResponse.message);
            dispatch(deleteUserStateReset());
            setDeleted((prev) => !prev);
        }
        if (deleteUserError) {
            Swal.fire(deleteUserError.data);
            dispatch(deleteUserStateReset());
        }

    }, [deleteUserResponse, deleteUserError])
    return (
        <div className="common-table-wrapper">
            <div className="common-table-filter-wrapper">
                <div className="common-left-blk">
                    <h2 className='fs-5 fw-bold mb-0'>{title} List</h2>
                </div>
                <div className="common-right-blk">
                    <div className="common-sort-blk">
                        <label className="d-flex align-items-center gap-2">Show:
                            <select
                                className="form-select"
                                value={limit}
                                onChange={(e) => {
                                    setLimit(Number(e.target.value));
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={15}>15</option>
                                <option value={20}>20</option>
                            </select>
                        </label>
                    </div>
                </div>
            </div>
            <div className="table-blk table-responsive">
                <table className="table">
                    <thead>
                        <tr>
                            {columns?.map((col, index) => (
                                <th key={index}>{col}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {Array.isArray(tableData) && tableData.length > 0 ? (
                            tableData?.map((row, index) => (
                                <tr key={index}>
                                    {renderRow(row)}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="100%" style={{ textAlign: 'center' }}>No data available</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} items={items} limit={limit} handlePageChange={handlePageChange} />
        </div>
    )
}

export default Table
